import { isPartnerScopedRole } from './authorization'

const ROUTE_STATE_KEY = 'sofera:route_state:v1'

const ALLOWED_VIEWS = new Set([
  'login',
  'reset_password',
  'accept_invitation',
  'invitation_confirmed',
  'invitation_invalid',
  'impersonation_callback',
  'central_auth_callback',
  'student_home',
  'student_course_view',
  'student_courses',
  'student_enrollments',
  'student_certs',
  'access_denied',
  'public_verify',
  'admin_dashboard',
  'admin_reports',
  'admin_companies',
  'admin_users',
  'admin_users_ro',
  'admin_communications',
  'admin_whatsapp',
  'admin_certs',
  'admin_certs_issued',
  'admin_certs_manual',
  'admin_certs_models',
  'admin_partners',
  'admin_categories',
  'admin_connections',
  'admin_courses',
  'admin_classes',
  'admin_class_details',
  'admin_enrollments',
  'admin_library',
  'admin_assessments',
  'admin_service_orders',
  'admin_invites',
  'admin_idiomas',
])

function normalizeId(value) {
  if (value == null) return null
  const s = String(value).trim()
  if (!s) return null
  const n = Number(s)
  if (Number.isFinite(n) && String(n) === s) return n
  return s
}

function pick(obj, keys) {
  try {
    const out = {}
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(obj || {}, k)) out[k] = obj[k]
    }
    return out
  } catch {
    return null
  }
}

export function sanitizeSnapshot(view, snapshot) {
  try {
    const v = sanitizeView(view)
    if (!v) return null
    if (!snapshot || typeof snapshot !== 'object') return null
    if (v === 'admin_class_details') {
      const picked = pick(snapshot, ['id', 'name', 'coordinator', 'instructor', 'duration', 'students', 'courses', 'status', 'companyId', 'partnerId'])
      const id = normalizeId(picked?.id)
      if (id == null) return null
      return { ...picked, id }
    }
    if (v === 'student_course_view') {
      const picked = pick(snapshot, ['id', 'title', 'description', 'progress', 'thumb', 'workload', 'instructor', 'coordinator', 'templateId', 'status'])
      const id = normalizeId(picked?.id)
      if (id == null) return null
      return { ...picked, id }
    }
    return null
  } catch {
    return null
  }
}

export function sanitizeParams(view, params) {
  try {
    const v = sanitizeView(view)
    if (!v) return null
    const p = params && typeof params === 'object' ? params : {}
    if (v === 'admin_class_details') {
      const classId = normalizeId(p.classId)
      return classId == null ? null : { classId }
    }
    if (v === 'student_course_view') {
      const courseId = normalizeId(p.courseId)
      if (courseId == null) return null
      const classId = normalizeId(p.classId)
      return classId == null ? { courseId } : { courseId, classId }
    }
    if (v === 'accept_invitation') {
      const token = typeof p.token === 'string' ? p.token : ''
      return token ? { token } : {}
    }
    return null
  } catch {
    return null
  }
}

export function isSessionValid(session, { nowMs = Date.now(), skewSeconds = 30 } = {}) {
  try {
    if (!session) return false
    const exp = session.expires_at
    if (!exp) return true
    const expMs = Number(exp) * 1000
    if (!Number.isFinite(expMs)) return true
    return expMs > (nowMs + skewSeconds * 1000)
  } catch {
    return false
  }
}

export function hasInviteHintsInUrl(loc = window.location) {
  try {
    const search = String(loc?.search || '')
    const hash = String(loc?.hash || '')
    const combined = `${search}&${hash}`.toLowerCase()
    return (
      combined.includes('invite_code=') ||
      combined.includes('type=invite') ||
      combined.includes('token_hash=') ||
      combined.includes('access_token=') ||
      combined.includes('refresh_token=') ||
      combined.includes('code=')
    )
  } catch {
    return false
  }
}

export function parseVerifyFromLocation(locationLike) {
  try {
    const loc = locationLike || (typeof window !== 'undefined' ? window.location : null)
    if (!loc) return null

    const pathname = String(loc.pathname || '')
    const search = String(loc.search || '')
    const hash = String(loc.hash || '')

    if (hash.startsWith('#verify')) {
      const params = new URLSearchParams(hash.split('?')[1] || '')
      const code = params.get('code') || ''
      return { code }
    }

    if (pathname === '/verify' || pathname === '/verify/' || pathname === '/validar' || pathname === '/validar/') {
      const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      const code = params.get('code') || ''
      const action = params.get('action') || ''
      return { code, action }
    }

    return null
  } catch {
    return null
  }
}

export function parseInternalRouteFromLocation(locationLike) {
  try {
    const loc = locationLike || (typeof window !== 'undefined' ? window.location : null)
    if (!loc) return null
    const search = String(loc.search || '')
    const hash = String(loc.hash || '')
    const searchParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
    const authType = (searchParams.get('type') || hashParams.get('type') || '').toLowerCase()
    const hasRecoveryHints = authType === 'recovery'
      || (
        (searchParams.has('access_token') || hashParams.has('access_token') || searchParams.has('refresh_token') || hashParams.has('refresh_token') || searchParams.has('code') || hashParams.has('code') || searchParams.has('token_hash') || hashParams.has('token_hash'))
        && authType !== 'invite'
      )
    
    // Tenta primeiro o pathname (URL limpa)
    const rawPath = String(loc.pathname || '')
    let pathname = rawPath.endsWith('/') && rawPath !== '/' ? rawPath.slice(0, -1) : rawPath
    
    // Se o pathname for vazio/raiz, tenta o hash (caso o usuário esteja usando rotas via hash)
    if ((pathname === '/' || !pathname) && loc.hash && loc.hash.includes('/')) {
      const hashPart = loc.hash.startsWith('#') ? loc.hash.slice(1) : loc.hash
      pathname = hashPart.split('?')[0]
      if (pathname.endsWith('/') && pathname !== '/') pathname = pathname.slice(0, -1)
    }

    const parts = pathname.split('/').filter(Boolean)
    if (parts.length === 0) {
      if (hasRecoveryHints) return { view: 'reset_password', params: null }
      return null
    }

    // Se o app estiver em uma subpasta, ignora o primeiro segmento se não for um dos esperados
    let head = parts[0]
    let subIdx = 1
    const knownHeads = ['admin', 'courses', 'course', 'reset', 'reset-password', 'accept-invitation', 'invitation-confirmed', 'invitation-invalid', 'impersonation', 'auth']
    if (!knownHeads.includes(head) && parts.length > 1 && knownHeads.includes(parts[1])) {
      head = parts[1]
      subIdx = 2
    }
    
    if (head === 'reset' || head === 'reset-password') return { view: 'reset_password', params: null }
    if (head === 'accept-invitation') {
      let token = ''
      // Prioridade: último segmento que pareça UUID após o último 'accept-invitation'
      const aiIdxs = parts.reduce((acc, seg, i) => (seg === 'accept-invitation' ? acc.concat(i) : acc), [])
      if (aiIdxs.length > 0) {
        const lastIdx = aiIdxs[aiIdxs.length - 1]
        token = parts[lastIdx + 1] || ''
        // Se não for UUID, tenta varrer todos os segmentos
        const uuidRe = /^[0-9a-fA-F-]{32,36}$/
        if (!uuidRe.test(token)) {
          const candidate = parts.find(s => uuidRe.test(s))
          if (candidate) token = candidate
        }
      } else {
        token = parts[subIdx] || ''
      }
      if (!token) {
        try {
          const qs = String(loc.search || '')
          const params = new URLSearchParams(qs.startsWith('?') ? qs.slice(1) : qs)
          token = params.get('token') || ''
        } catch {}
      }
      if (!token) {
        // Links de convite podem chegar apenas via query/hash.
        if (hasInviteHintsInUrl(loc)) return { view: 'accept_invitation', params: {} }
        // Keep backward compatibility: also allow direct /accept-invitation without token.
        return { view: 'accept_invitation', params: {} }
      }
      return { view: 'accept_invitation', params: { token } }
    }
    if (head === 'invitation-confirmed') return { view: 'invitation_confirmed', params: null }
    if (head === 'invitation-invalid') return { view: 'invitation_invalid', params: null }

    if (head === 'impersonation') {
      const sub = parts[subIdx] || ''
      if (sub === 'callback') return { view: 'impersonation_callback', params: null }
    }

    if (head === 'auth') {
      const sub = parts[subIdx] || ''
      if (sub === 'callback') return { view: 'central_auth_callback', params: null }
    }
    
    // Suporte a views diretas (ex: /admin_users)
    if (head.startsWith('admin_') && ALLOWED_VIEWS.has(head)) return { view: head, params: null }

    if (head === 'admin') {
      const sub = parts[subIdx] || ''
      if (!sub) return { view: 'admin_dashboard', params: null }
      if (sub === 'dashboard') return { view: 'admin_dashboard', params: null }
      if (sub === 'reports') return { view: 'admin_reports', params: null }
      if (sub === 'companies') return { view: 'admin_companies', params: null }
      if (sub === 'users') return { view: 'admin_users', params: null }
      if (sub === 'communications') return { view: 'admin_communications', params: null }
      if (sub === 'whatsapp') return { view: 'admin_whatsapp', params: null }
      if (sub === 'partners') return { view: 'admin_partners', params: null }
      if (sub === 'categories') return { view: 'admin_categories', params: null }
      if (sub === 'connections' || sub === 'integracoes') return { view: 'admin_connections', params: null }
      if (sub === 'idiomas') return { view: 'admin_idiomas', params: null }
      if (sub === 'invites') return { view: 'admin_invites', params: null }
      if (sub === 'service_orders' || sub === 'service-orders') return { view: 'admin_service_orders', params: null }
      if (sub === 'courses') return { view: 'admin_courses', params: null }
      if (sub === 'enrollments') return { view: 'admin_enrollments', params: null }
      if (sub === 'library') return { view: 'admin_library', params: null }
      if (sub === 'assessments') return { view: 'admin_assessments', params: null }
      if (sub === 'classes') {
        const id = normalizeId(parts[subIdx + 1])
        if (id == null) return { view: 'admin_classes', params: null }
        return { view: 'admin_class_details', params: { classId: id } }
      }
      if (sub === 'certs' || sub === 'certificates') {
        const mode = parts[subIdx + 1] || ''
        if (mode === 'models') return { view: 'admin_certs_models', params: null }
        if (mode === 'manual') return { view: 'admin_certs_manual', params: null }
        if (mode === 'issued') return { view: 'admin_certs_issued', params: null }
        return { view: 'admin_certs', params: null }
      }

      return null
    }

    if (head === 'courses' || head === 'course') {
      const id = normalizeId(parts[subIdx])
      if (id == null) return { view: 'student_courses', params: null }
      // O link de "Convite Link" carrega a turma em `?turma=` (ex.: /courses/{id}?turma={classId}).
      // Preservar esse id é o que garante que a rota sobreviva ao login e volte para a tela do código.
      const classId = normalizeId(searchParams.get('turma'))
      return { view: 'student_course_view', params: classId ? { courseId: id, classId } : { courseId: id } }
    }
    if (head === 'enrollments' || head === 'matriculas') {
      return { view: 'student_enrollments', params: null }
    }
    if (head === 'certs' || head === 'certificates') {
      return { view: 'student_certs', params: null }
    }

    return null
  } catch {
    return null
  }
}

export function viewToPath(view, { classId, courseId, verifyCode, token } = {}) {
  const v = sanitizeView(view)
  if (!v) return null

  if (v === 'login') return '/'
  if (v === 'impersonation_callback') return '/impersonation/callback'
  if (v === 'central_auth_callback') return '/auth/callback'
  if (v === 'reset_password') {
    return '/reset'
  }
  if (v === 'student_home') return '/'

  if (v === 'public_verify') {
    const code = typeof verifyCode === 'string' ? verifyCode : ''
    const action = new URLSearchParams(window?.location?.search || '').get('action');
    const actionParam = action ? `&action=${encodeURIComponent(action)}` : '';
    return code ? `/validar?code=${encodeURIComponent(code)}${actionParam}` : `/validar${actionParam ? '?' + actionParam.slice(1) : ''}`
  }

  if (v === 'accept_invitation') {
    const inviteToken = typeof token === 'string' ? token : ''
    return inviteToken ? `/accept-invitation/${encodeURIComponent(inviteToken)}` : '/accept-invitation'
  }

  if (v === 'invitation_confirmed') return '/invitation-confirmed'
  if (v === 'invitation_invalid') return '/invitation-invalid'

  if (v === 'student_course_view') {
    const id = normalizeId(courseId)
    if (id == null) return '/'
    const base = `/courses/${encodeURIComponent(String(id))}`
    const cls = normalizeId(classId)
    return cls == null ? base : `${base}?turma=${encodeURIComponent(String(cls))}`
  }
  if (v === 'student_courses') return '/courses'
  if (v === 'student_enrollments') return '/enrollments'
  if (v === 'student_certs') return '/certificates'

  if (v === 'access_denied') return '/'

  if (!v.startsWith('admin_')) return '/'

  if (v === 'admin_dashboard') return '/admin/dashboard'
  if (v === 'admin_reports') return '/admin/reports'
  if (v === 'admin_companies') return '/admin/companies'
  if (v === 'admin_users') return '/admin/users'
  if (v === 'admin_users_ro') return '/admin/users'
  if (v === 'admin_communications') return '/admin/communications'
  if (v === 'admin_whatsapp') return '/admin/whatsapp'
  if (v === 'admin_partners') return '/admin/partners'
  if (v === 'admin_categories') return '/admin/categories'
  if (v === 'admin_connections') return '/admin/connections'
  if (v === 'admin_courses') return '/admin/courses'
  if (v === 'admin_enrollments') return '/admin/enrollments'
  if (v === 'admin_library') return '/admin/library'
  if (v === 'admin_assessments') return '/admin/assessments'
  if (v === 'admin_classes') return '/admin/classes'
  if (v === 'admin_class_details') {
    const id = normalizeId(classId)
    if (id == null) return '/admin/classes'
    return `/admin/classes/${encodeURIComponent(String(id))}`
  }

  if (v === 'admin_service_orders') return '/admin/service_orders'
  if (v === 'admin_idiomas') return '/admin/idiomas'
  if (v === 'admin_invites') return '/admin/invites'

  if (v === 'admin_certs') return '/admin/certs'
  if (v === 'admin_certs_issued') return '/admin/certs/issued'
  if (v === 'admin_certs_manual') return '/admin/certs/manual'
  if (v === 'admin_certs_models') return '/admin/certs/models'

  return '/admin/dashboard'
}

export function sanitizeView(view) {
  if (typeof view !== 'string') return null
  if (!ALLOWED_VIEWS.has(view)) return null
  return view
}

export function resolveRestoredView(view, { isAdmin, roleName, permissionsJson } = {}) {
  const v = sanitizeView(view)
  if (!v) return null
  
  // Se o roleName não chegou ainda (está carregando), e é uma view de admin, deixamos passar por enquanto.
  // O App.jsx vai re-avaliar assim que o roleName e permissionsJson forem carregados de fato.
  if (v.startsWith('admin_') && !isAdmin && !roleName) {
    return v;
  }

  const isPureStudent = !isAdmin && (!roleName || roleName === 'Aluno')
  
  if (isPureStudent) {
    // Aluno nunca deve cair em uma view de admin ao restaurar a rota pós-login.
    // Em vez de exibir "Acesso negado", redirecionamos para a home do aluno.
    if (v.startsWith('admin_') || v === 'admin_dashboard' || v === 'access_denied') return 'student_home'
  } else {
    // É um usuário administrativo (Parceiro, Coordenador, etc).
    // Não devemos forçá-lo para admin_dashboard se ele tentar acessar uma view válida que ele tem permissão.
    // Mas devemos convertê-lo de student_home para admin_dashboard
    if (v === 'student_home' || v === 'access_denied') return 'admin_dashboard'
    
    // Validar se ele tem permissão para a view de admin solicitada
    if (!isAdmin && v.startsWith('admin_') && v !== 'admin_dashboard') {
      const p = permissionsJson || {}
      let allowed = false
      // Ponto Focal e Parceiro SAS usam a view editável com escopo controlado
      if ((roleName === 'Ponto Focal' || roleName === 'Parceiro SAS') && v === 'admin_users_ro') {
        return 'admin_users'
      }
      // Gestor Parceiro permanece na view somente leitura
      if (roleName === 'Gestor Parceiro' && v === 'admin_users') {
        return 'admin_users_ro'
      }
      
      switch (v) {
        case 'admin_reports': allowed = p.view_linked_companies_reports || p.export_reports || p.view_students_progress || p.view_own_progress; break;
        case 'admin_courses': allowed = p.create_courses || p.edit_courses; break;
        case 'admin_enrollments': allowed = p.enroll_students || roleName === 'Ponto Focal' || isPartnerScopedRole(roleName); break;
        case 'admin_users_ro': allowed = roleName === 'Gestor Parceiro'; break;
        case 'admin_classes': allowed = p.create_classes; break;
        case 'admin_class_details': allowed = p.create_classes; break;
        case 'admin_library': allowed = p.create_lessons || p.download_materials; break;
        case 'admin_assessments': allowed = p.create_exams; break;
        case 'admin_certs':
        case 'admin_certs_issued': allowed = p.view_company_certificates || p.download_company_certificates; break;
        case 'admin_certs_manual': allowed = p.issue_certificates; break;
        case 'admin_certs_models': allowed = p.create_courses; break;
        case 'admin_service_orders': allowed = p.open_os || p.view_own_os || p.manage_all_os; break;
        case 'admin_companies': allowed = p.view_linked_companies || p.create_company || p.edit_company; break;
        case 'admin_users': allowed = roleName === 'Ponto Focal' || roleName === 'Parceiro SAS' || p.create_users || p.edit_users || p.deactivate_users || p.view_linked_companies; break;
        case 'admin_communications': allowed = p.configure_system; break;
        case 'admin_whatsapp': allowed = p.configure_system; break;
        case 'admin_partners': allowed = p.configure_system; break;
        case 'admin_categories': allowed = p.configure_system; break;
        case 'admin_connections': allowed = p.configure_system; break;
        case 'admin_invites': allowed = p.configure_system; break;
        case 'admin_idiomas': allowed = p.configure_system; break;
        default: allowed = true;
      }

      // Bloqueio explícito para o perfil "Ponto Focal":
      // não pode abrir Gestão de OS nem Gestão Admin em nenhuma circunstância
      if (roleName === 'Ponto Focal') {
        if (['admin_service_orders','admin_companies','admin_communications','admin_whatsapp','admin_partners','admin_categories','admin_connections','admin_invites','admin_idiomas'].includes(v)) {
          allowed = false;
        }
      }
      
      // Regra de segurança extra para Parceiro SAS: se a rota não foi mapeada especificamente ou foi negada
      // Mas ele tem view_linked_companies (é parceiro), algumas views são liberadas
      if (!allowed && isPartnerScopedRole(roleName)) {
         if (['admin_users', 'admin_companies', 'admin_service_orders', 'admin_certs_issued', 'admin_enrollments'].includes(v)) {
            allowed = true;
         }
      }

      if (!allowed) return 'admin_dashboard'
    }
  }
  
  return v
}

export function loadRouteState(storage) {
  try {
    const s = storage || (typeof window !== 'undefined' ? window.sessionStorage : null)
    if (!s) return null
    const raw = s.getItem(ROUTE_STATE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const view = sanitizeView(parsed?.view)
    const postLoginView = sanitizeView(parsed?.postLoginView)
    const params = sanitizeParams(view, parsed?.params)
    const snapshot = sanitizeSnapshot(view, parsed?.snapshot)
    const url = (typeof parsed?.url === 'string') ? parsed.url : null
    const ts = (typeof parsed?.ts === 'number') ? parsed.ts : null
    return { view, postLoginView, params, snapshot, url, ts }
  } catch {
    return null
  }
}

export function saveRouteState({ view, postLoginView, params, snapshot } = {}, storage) {
  try {
    const s = storage || (typeof window !== 'undefined' ? window.sessionStorage : null)
    if (!s) return
    const state = { view: sanitizeView(view), postLoginView: sanitizeView(postLoginView), params: sanitizeParams(view, params), snapshot: sanitizeSnapshot(view, snapshot), url: (typeof window !== 'undefined' ? window.location.href : null), ts: Date.now() }
    s.setItem(ROUTE_STATE_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

export function clearRouteState(storage) {
  try {
    const s = storage || (typeof window !== 'undefined' ? window.sessionStorage : null)
    if (!s) return
    s.removeItem(ROUTE_STATE_KEY)
  } catch {
    // ignore
  }
}
